import { queryOptions } from '@tanstack/react-query';

import { ONE_HOUR } from '@/constants/time';
import { paths } from '@primum/api-types';
import type { PublicationShares } from '@/types/messages/base';
import { fetchClient } from '@/util/fetchClient';

type GetMessagePublicationsV2Response =
  paths['/v2/conversation-messages/{messageId}/publications']['get']['responses'][200]['content']['application/json']['data'];

export type MessagePublication = NonNullable<GetMessagePublicationsV2Response>['publications'][0];

export type PublicationSummary = NonNullable<MessagePublication['summary']>;

export async function getMessagePublicationsV2({
  conversationMessageId,
}: Pick<PublicationShares, 'conversationMessageId'>) {
  const { data } = await fetchClient.GET('/v2/conversation-messages/{messageId}/publications', {
    params: {
      path: { messageId: conversationMessageId },
    },
  });

  return data?.data?.publications ?? [];
}

/**
 * Keyed on the attribute's change stamp as well as the message, because the
 * backend moves it whenever the share list changes. A changed list therefore
 * arrives as a new key through the message attribute update, which is why the
 * data can stay fresh for an hour without polling or invalidation.
 */
export function getMessagePublicationsV2QueryOptions({
  conversationMessageId,
  lastAddedAt,
  updatedAt,
}: Pick<PublicationShares, 'conversationMessageId' | 'lastAddedAt' | 'updatedAt'>) {
  return queryOptions({
    queryKey: ['messagePublicationsV2', conversationMessageId, updatedAt ?? lastAddedAt],
    queryFn: () => getMessagePublicationsV2({ conversationMessageId }),
    staleTime: ONE_HOUR,
    gcTime: ONE_HOUR,
  });
}
